import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import '../../assets/css/elements/phone_input.css';

// Common country codes - can be extended as needed
const COUNTRY_CODES = [
    { code: '+234', country: 'Nigeria', flag: '🇳🇬', maxLength: 10 },
    { code: '+1', country: 'USA/Canada', flag: '🇺🇸', maxLength: 10 },
    { code: '+44', country: 'United Kingdom', flag: '🇬🇧', maxLength: 10 },
    { code: '+233', country: 'Ghana', flag: '🇬🇭', maxLength: 9 },
    { code: '+254', country: 'Kenya', flag: '🇰🇪', maxLength: 9 },
    { code: '+27', country: 'South Africa', flag: '🇿🇦', maxLength: 9 },
    { code: '+91', country: 'India', flag: '🇮🇳', maxLength: 10 },
    { code: '+86', country: 'China', flag: '🇨🇳', maxLength: 11 },
    { code: '+49', country: 'Germany', flag: '🇩🇪', maxLength: 11 },
    { code: '+33', country: 'France', flag: '🇫🇷', maxLength: 9 },
    { code: '+971', country: 'UAE', flag: '🇦🇪', maxLength: 9 },
    { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦', maxLength: 9 },
];

/**
 * Normalizes phone number by removing leading zeros and country codes
 * @param {string} phone - The raw phone number input
 * @param {string} countryCode - The selected country code (e.g., '+234')
 * @returns {string} - Normalized phone number (digits only, no leading zeros or country code)
 */
const normalizePhoneNumber = (phone, countryCode) => {
    // Remove all non-digit characters
    let normalized = phone.replace(/\D/g, '');
    
    // Remove country code if present (without +)
    const codeWithoutPlus = countryCode.replace('+', '');
    if (normalized.startsWith(codeWithoutPlus)) {
        normalized = normalized.substring(codeWithoutPlus.length);
    }
    
    // Remove leading zeros
    normalized = normalized.replace(/^0+/, '');
    
    return normalized;
};

/**
 * Formats phone number for display with spaces
 * @param {string} phone - Normalized phone number
 * @returns {string} - Formatted phone number
 */
const formatPhoneDisplay = (phone) => {
    if (!phone) return '';
    
    // Format as XXX XXX XXXX or similar
    if (phone.length <= 3) return phone;
    if (phone.length <= 6) return `${phone.slice(0, 3)} ${phone.slice(3)}`;
    return `${phone.slice(0, 3)} ${phone.slice(3, 6)} ${phone.slice(6)}`;
};

const PhoneInput = ({
    label,
    value = '',
    onChange,
    name,
    error,
    icon,
    required = false,
    disabled = false,
    className = '',
    showLabel = false,
    helpText,
    showHelpText = false,
    defaultCountryCode = '+234', // Nigeria as default
}) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState(
        COUNTRY_CODES.find(c => c.code === defaultCountryCode) || COUNTRY_CODES[0]
    );
    const [localPhone, setLocalPhone] = useState('');
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    // Parse incoming value to extract country code and phone on initial mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (value) {
            // Check if value starts with a country code
            let matchedCountry = null;
            let phoneNumber = value;
            
            for (const country of COUNTRY_CODES) {
                if (value.startsWith(country.code)) {
                    matchedCountry = country;
                    phoneNumber = value.substring(country.code.length);
                    break;
                }
            }
            
            if (matchedCountry) {
                setSelectedCountry(matchedCountry);
            }
            
            // Normalize and set local phone
            const normalized = normalizePhoneNumber(phoneNumber, selectedCountry.code);
            setLocalPhone(normalized);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Intentionally empty - only run on mount to parse initial value

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleCountrySelect = (country) => {
        setSelectedCountry(country);
        setIsDropdownOpen(false);
        
        // Re-emit the full phone number with new country code
        if (localPhone) {
            const fullPhone = `${country.code}${localPhone}`;
            onChange({
                target: {
                    name,
                    value: fullPhone
                }
            });
        }
        
        // Focus the input after selection
        inputRef.current?.focus();
    };

    const handlePhoneChange = (e) => {
        const rawValue = e.target.value;
        
        // Normalize the phone number
        const normalized = normalizePhoneNumber(rawValue, selectedCountry.code);
        
        // Limit to max length for the country
        const limited = normalized.substring(0, selectedCountry.maxLength);
        
        setLocalPhone(limited);
        
        // Emit the full phone number with country code
        const fullPhone = limited ? `${selectedCountry.code}${limited}` : '';
        onChange({
            target: {
                name,
                value: fullPhone
            }
        });
    };

    const handleKeyDown = (e) => {
        // Allow: backspace, delete, tab, escape, enter, and navigation keys
        if (
            e.key === 'Backspace' ||
            e.key === 'Delete' ||
            e.key === 'Tab' ||
            e.key === 'Escape' ||
            e.key === 'Enter' ||
            e.key === 'ArrowLeft' ||
            e.key === 'ArrowRight' ||
            e.key === 'Home' ||
            e.key === 'End' ||
            // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
            (e.ctrlKey && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase()))
        ) {
            return;
        }
        
        // Block non-numeric input
        if (!/^\d$/.test(e.key)) {
            e.preventDefault();
        }
    };

    return (
        <div className={`form-input-container phone-input-container ${className}`}>
            {label && showLabel && (
                <label className="form-input-label">
                    {label}
                    {required && <span className="required-star">*</span>}
                </label>
            )}
            
            <div className={`input-group phone-input-group ${error ? 'error' : ''} ${disabled ? 'disabled' : ''}`}>
                {icon && <span className="input-icon">{icon}</span>}
                
                {/* Country Code Selector */}
                <div className="country-selector" ref={dropdownRef}>
                    <button
                        type="button"
                        className="country-selector-btn"
                        onClick={() => !disabled && setIsDropdownOpen(!isDropdownOpen)}
                        disabled={disabled}
                        aria-label="Select country code"
                        aria-expanded={isDropdownOpen}
                    >
                        <span className="country-flag">{selectedCountry.flag}</span>
                        <span className="country-code">{selectedCountry.code}</span>
                        <span className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`}>▼</span>
                    </button>
                    
                    {isDropdownOpen && (
                        <div className="country-dropdown">
                            {COUNTRY_CODES.map((country) => (
                                <button
                                    key={country.code}
                                    type="button"
                                    className={`country-option ${country.code === selectedCountry.code ? 'selected' : ''}`}
                                    onClick={() => handleCountrySelect(country)}
                                >
                                    <span className="country-flag">{country.flag}</span>
                                    <span className="country-name">{country.country}</span>
                                    <span className="country-code">{country.code}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                
                {/* Phone Number Input */}
                <input
                    ref={inputRef}
                    type="tel"
                    name={name}
                    value={formatPhoneDisplay(localPhone)}
                    onChange={handlePhoneChange}
                    onKeyDown={handleKeyDown}
                    placeholder="XXX XXX XXXX"
                    disabled={disabled}
                    className="form-input phone-number-input"
                    required={required}
                    inputMode="numeric"
                    autoComplete="tel-national"
                />
            </div>
            
            {error && <span className="error-message">{error}</span>}
            {showHelpText && helpText && <small className="help-text">{helpText}</small>}
        </div>
    );
};

PhoneInput.propTypes = {
    /** Label text for the input */
    label: PropTypes.string,
    /** Current phone value (with country code) */
    value: PropTypes.string,
    /** Change handler function */
    onChange: PropTypes.func.isRequired,
    /** Input name attribute */
    name: PropTypes.string,
    /** Error message to display */
    error: PropTypes.string,
    /** Icon element to display */
    icon: PropTypes.node,
    /** Whether the field is required */
    required: PropTypes.bool,
    /** Whether the input is disabled */
    disabled: PropTypes.bool,
    /** Additional CSS class */
    className: PropTypes.string,
    /** Whether to show the label */
    showLabel: PropTypes.bool,
    /** Help text to display below input */
    helpText: PropTypes.string,
    /** Whether to show help text */
    showHelpText: PropTypes.bool,
    /** Default country code (e.g., '+234') */
    defaultCountryCode: PropTypes.string,
};

export default PhoneInput;
